import PropTypes from "prop-types";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Col, Row, Card, CardBody } from "reactstrap";
import HorizontalScroll from "../../components/Common/HorizontalScroll";
import SecondaryButton from "../../components/Common/SecondaryButton";
import "../../assets/scss/custom/components/top-rewards-widgets.scss";
import whiteArrow from "../../assets/images/user-dashboard/white-right-arrow.svg";
import fire from "../../assets/images/user-dashboard/flame.svg";
import check from "../../assets/images/user-dashboard/check.svg";
import removeCircle from "../../assets/images/user-dashboard/remove-circle.svg";
import link from "../../assets/images/user-dashboard/link.svg";
import wallet from "../../assets/images/user-dashboard/wallet-3.svg";
import connectedBadge from "../../assets/images/user-dashboard/connected-badge.svg";

/* -------------------------------------------------------------------------
 * Small presentational helpers
 * ---------------------------------------------------------------------- */

const ProgressBar = ({ percent, muted }) => (
  <div
    className={`reward-progress-track ${muted ? "reward-progress-track--muted" : ""}`}
  >
    <div
      className={`reward-progress-fill ${muted ? "reward-progress-fill--muted" : ""}`}
      style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
    />
  </div>
);

ProgressBar.propTypes = {
  percent: PropTypes.number.isRequired,
  muted: PropTypes.bool,
};

ProgressBar.defaultProps = {
  muted: false,
};

/* -------------------------------------------------------------------------
 * Card 1 — Account State
 * ---------------------------------------------------------------------- */

const AccountStateCard = ({
  connected,
  cashbackPercent,
  accountNumber,
  onConnect,
  onSettings,
}) => {
  const navigate = useNavigate();

  const handleToggle = () => {
    if (!connected) {
      onConnect();
    } else {
      navigate("/settings");
    }
  };

  return (
    <Card className="reward-card reward-card--account">
      <CardBody>
        <div className="reward-card__eyebrow">
          {connected ? "Account Linked" : "Account State"}
        </div>
        <h2 className="reward-card__title">
          {connected ? (
            <div className="reward-card__title-with-check">
              Account Connected
              <img
                src={connectedBadge}
                alt="connected"
                className="reward-check-icon"
              />
            </div>
          ) : (
            "Link your Account"
          )}
        </h2>
        {connected ? (
          <>
            <p className="reward-card__desc">Your account is linked</p>
            <p className="reward-card__desc">
              Earning {cashbackPercent}% back on every meal automatically
            </p>
          </>
        ) : (
          <p className="reward-card__desc">
            Earn {cashbackPercent}% back automatically
          </p>
        )}

        <div className="reward-card__body" />

        <div className="reward-card__footer-row">
          <div>
            {connected ? (
              <div className="reward-account-wrapper">
                <div className="reward-account-label">Account</div>
                <div className="reward-account-number">
                  {accountNumber || "**********4242"}
                </div>
              </div>
            ) : (
              <img src={wallet} alt="wallet" />
            )}
          </div>
          {connected ? (
            <SecondaryButton
              label="Go to Settings"
              icon={whiteArrow}
              variant="orange"
              onClick={handleToggle}
            />
          ) : (
            <SecondaryButton
              label="Connect Account"
              icon={link}
              variant="orange"
              onClick={handleToggle}
            />
          )}
        </div>
      </CardBody>
    </Card>
  );
};

AccountStateCard.propTypes = {
  connected: PropTypes.bool,
  cashbackPercent: PropTypes.number,
  accountNumber: PropTypes.string,
  onConnect: PropTypes.func,
  onSettings: PropTypes.func,
};

AccountStateCard.defaultProps = {
  connected: false,
  cashbackPercent: 5,
  accountNumber: null,
  onConnect: () => {},
  onSettings: () => {},
};

/* -------------------------------------------------------------------------
 * Card 2 — Dining Streak (Game)
 * ---------------------------------------------------------------------- */

const DiningStreakCard = ({
  loggedIn,
  name,
  description,
  weeksCompleted,
  totalWeeks,
  pointsReward,
  weeklyCompletion,
  onViewAll,
  onSignUp,
}) => {
  const now = new Date();

  const getWeekState = (week) => {
    if (week.completed) {
      return "done";
    }
    // Check if week is failed (not completed and start date has passed)
    if (week.start) {
      const weekStartDate = new Date(week.start);
      const weekEndDate = new Date(weekStartDate);
      weekEndDate.setDate(weekEndDate.getDate() + 7);
      if (now > weekEndDate) {
        return "failed";
      }
    }
    return "pending";
  };

  return (
    <Card className="reward-card reward-card--streak">
      <CardBody>
        <div className="reward-card__header">
          <div className="reward-card__eyebrow">Game</div>
          {loggedIn ? (
            <SecondaryButton
              label="View All"
              icon={whiteArrow}
              variant="green"
              className="secondary-btn--header"
              onClick={onViewAll}
            />
          ) : (
            <SecondaryButton
              label="Sign Up"
              icon={whiteArrow}
              variant="green"
              className="secondary-btn--header"
              onClick={onSignUp}
            />
          )}
        </div>
        <h2 className="reward-card__title">
          {loggedIn
            ? name || `${totalWeeks}-Week Dining Streak`
            : "Start Your Streak"}
        </h2>
        <p className="reward-card__desc">
          {loggedIn
            ? description ||
              `Dine at any partner restaurant once a week for ${totalWeeks} consecutive weeks.`
            : "Sign up to track your dining streak and earn bonus points every week."}
        </p>

        <div className="reward-card__body" />

        <div className="streak-weeks">
          {(
            weeklyCompletion ||
            Array.from({ length: totalWeeks }, (_, i) => ({
              week: i + 1,
              completed: i < weeksCompleted,
            }))
          ).map((week) => {
            const state = getWeekState(week);
            let iconSrc;
            if (state === "done") {
              iconSrc = check;
            } else if (state === "failed") {
              iconSrc = removeCircle;
            } else {
              iconSrc = fire;
            }

            return (
              <div
                key={week.week}
                className={`streak-week-chip streak-week-chip--${state}`}
              >
                <div className="streak-week-chip__icon">
                  <img src={iconSrc} alt="streak" />
                </div>
                <div className="streak-week-chip__label">Week {week.week}</div>
              </div>
            );
          })}
        </div>

        <p className="reward-card__footnote">
          {loggedIn
            ? `Complete to earn ${pointsReward.toLocaleString()} pts!`
            : "Sign up to start earning!"}
        </p>
      </CardBody>
    </Card>
  );
};

DiningStreakCard.propTypes = {
  loggedIn: PropTypes.bool,
  name: PropTypes.string,
  description: PropTypes.string,
  weeksCompleted: PropTypes.number,
  totalWeeks: PropTypes.number,
  pointsReward: PropTypes.number,
  weeklyCompletion: PropTypes.arrayOf(
    PropTypes.shape({
      week: PropTypes.number.isRequired,
      completed: PropTypes.bool.isRequired,
    }),
  ),
  onViewAll: PropTypes.func,
  onSignUp: PropTypes.func,
};

DiningStreakCard.defaultProps = {
  loggedIn: true,
  weeksCompleted: 0,
  totalWeeks: 4,
  pointsReward: 1000,
  onViewAll: () => {},
  onSignUp: () => {},
};

/* -------------------------------------------------------------------------
 * Card 3 — Passport Challenge
 * ---------------------------------------------------------------------- */

const PassportChallengeCard = ({
  loggedIn,
  name,
  description,
  visited,
  total,
  pointsReward,
  onViewAll,
}) => {
  const percent = (visited / total) * 100;
  return (
    <Card className="reward-card reward-card--challenge">
      <CardBody>
        <div className="reward-card__header">
          <div className="reward-card__eyebrow">Challenge</div>
          {loggedIn && (
            <SecondaryButton
              label="History"
              icon={whiteArrow}
              variant="brown"
              className="secondary-btn--header"
              onClick={onViewAll}
            />
          )}
        </div>
        <h2 className="reward-card__title">{name || "Omaha Passport"}</h2>
        <p className="reward-card__desc">
          {loggedIn
            ? description ||
              `Visit all ${total} partner restaurants to become a Legend.`
            : "Login to unlock the Omaha Passport challenge and become a Legend."}
        </p>

        <div className="reward-card__body" />

        <div>
          <div
            className={`reward-progress-row ${
              loggedIn ? "" : "reward-progress-row--muted"
            }`}
          >
            <span className="reward-progress-row__label">
              Visit all {total} partner restaurants.
            </span>
            <span className="reward-progress-row__value">
              {visited} / {total}
            </span>
          </div>
          <ProgressBar percent={percent} muted={!loggedIn} />
        </div>

        {loggedIn ? (
          <p className="reward-card__footnote">
            Complete to earn {pointsReward.toLocaleString()} pts!
          </p>
        ) : (
          <p className="reward-card__footnote reward-card__footnote--muted">
            Login to unlock <img src={whiteArrow} alt="arrow-right" />
          </p>
        )}
      </CardBody>
    </Card>
  );
};

PassportChallengeCard.propTypes = {
  loggedIn: PropTypes.bool,
  name: PropTypes.string,
  description: PropTypes.string,
  visited: PropTypes.number,
  total: PropTypes.number,
  pointsReward: PropTypes.number,
  onViewAll: PropTypes.func,
};

PassportChallengeCard.defaultProps = {
  loggedIn: true,
  visited: 0,
  total: 200,
  pointsReward: 50000,
  onViewAll: () => {},
};

/* -------------------------------------------------------------------------
 * Card 4 — Reward Points
 * ---------------------------------------------------------------------- */

const RewardPointsCard = ({
  loggedIn,
  points,
  cashbackPercent,
  tiers,
  onRedeem,
  onJoinFree,
}) => {
  // Find the next tier based on current points
  const nextTier =
    tiers.find((tier) => tier.points > points) || tiers[tiers.length - 1];
  const currentTierIndex = tiers.findIndex((tier) => tier.points > points) - 1;
  const currentTier = currentTierIndex >= 0 ? tiers[currentTierIndex] : null;

  // Calculate progress
  let percent = 0;
  let pointsToGo = 0;

  if (loggedIn) {
    if (currentTier && nextTier) {
      // Progress from current tier to next tier
      const pointsInCurrentTier = points - currentTier.points;
      const pointsBetweenTiers = nextTier.points - currentTier.points;
      percent = Math.min(100, (pointsInCurrentTier / pointsBetweenTiers) * 100);
      pointsToGo = nextTier.points - points;
    } else if (!currentTier) {
      // Before first tier
      percent = Math.min(100, (points / nextTier.points) * 100);
      pointsToGo = nextTier.points - points;
    } else {
      // At or beyond last tier
      percent = 100;
      pointsToGo = 0;
    }
  } else {
    percent = 100;
  }

  return (
    <Card className="reward-card reward-card--points">
      <CardBody>
        <div className="reward-card__header">
          <div className="reward-card__eyebrow">Reward</div>
          {loggedIn ? (
            <SecondaryButton
              label="Redeem"
              icon={whiteArrow}
              variant="purple"
              className="secondary-btn--header"
              onClick={onRedeem}
            />
          ) : (
            <SecondaryButton
              label="Join Free"
              icon={whiteArrow}
              variant="purple"
              className="secondary-btn--header"
              onClick={onJoinFree}
            />
          )}
        </div>
        <h2 className="reward-card__title" style={{ marginBottom: 0 }}>
          {loggedIn ? "Available Points" : "Earn Points"}
        </h2>
        <div className="reward-points-value">
          {loggedIn ? (
            <>
              {points.toLocaleString()}
              <span className="reward-points-dollar">
                =${Math.floor(points / 100)}
              </span>
            </>
          ) : (
            `${cashbackPercent}%`
          )}
        </div>

        <div className="reward-card__body" />

        <div>
          <div className="reward-progress-row">
            <span className="reward-progress-row__label">
              Next Reward: ${nextTier.value}
            </span>
            <span className="reward-progress-row__value">
              {nextTier.points.toLocaleString()} pts
            </span>
          </div>
          <ProgressBar percent={percent} muted={!loggedIn} />
        </div>

        <p className="reward-card__footnote">
          {loggedIn
            ? pointsToGo > 0
              ? `${pointsToGo.toLocaleString()} points to go`
              : "Max tier reached"
            : "back on every meal"}
        </p>
      </CardBody>
    </Card>
  );
};

RewardPointsCard.propTypes = {
  loggedIn: PropTypes.bool,
  points: PropTypes.number,
  cashbackPercent: PropTypes.number,
  tiers: PropTypes.arrayOf(
    PropTypes.shape({
      points: PropTypes.number.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ).isRequired,
  onRedeem: PropTypes.func,
  onJoinFree: PropTypes.func,
};

RewardPointsCard.defaultProps = {
  loggedIn: true,
  points: 0,
  cashbackPercent: 5,
  tiers: [
    { points: 3500, value: 25 },
    { points: 6000, value: 50 },
    { points: 10000, value: 100 },
  ],
  onRedeem: () => {},
  onJoinFree: () => {},
};

/* -------------------------------------------------------------------------
 * Main widget — drop-in replacement for the old MiniWidget on Dashboard.js
 * ---------------------------------------------------------------------- */

const TopRewardsWidget = ({
  data,
  isLoggedIn,
  onConnectAccount,
  onSettings,
  onViewStreak,
  onSignUpStreak,
  onViewChallenge,
  onRedeemPoints,
  onJoinFree,
}) => {
  const { accountState, diningStreak, passportChallenge, rewardPoints } = data;

  // Check if there are any games to show
  const hasGameData =
    diningStreak && (diningStreak.name || diningStreak.totalWeeks > 0);
  const hasChallengeData =
    passportChallenge && passportChallenge.name && passportChallenge.total > 0;
  const showGameCards = hasGameData || hasChallengeData;

  // Calculate total number of cards showing
  const totalCards = showGameCards
    ? (hasGameData ? 1 : 0) + (hasChallengeData ? 1 : 0) + 2 // +2 for first and last cards
    : 2; // Only first and last cards

  // Calculate column size based on total cards (12 columns total)
  const colSize = Math.floor(12 / totalCards);

  // Adjust column sizing based on number of cards
  const firstCardColProps = { md: 6, lg: 6, xxl: colSize };
  const lastCardColProps = { md: 6, lg: 6, xxl: colSize };

  return (
    <HorizontalScroll className="rewards-scroll-container">
      <Row className="g-3">
        <Col {...firstCardColProps}>
          <AccountStateCard
            connected={accountState.connected}
            cashbackPercent={accountState.cashbackPercent}
            accountNumber={accountState.accountNumber}
            onConnect={onConnectAccount}
            onSettings={onSettings}
          />
        </Col>
        {showGameCards && (
          <>
            {hasGameData && (
              <Col md={6} lg={6} xxl={colSize}>
                <DiningStreakCard
                  loggedIn={isLoggedIn}
                  name={diningStreak.name}
                  description={diningStreak.description}
                  weeksCompleted={diningStreak.weeksCompleted}
                  totalWeeks={diningStreak.totalWeeks}
                  pointsReward={diningStreak.pointsReward}
                  weeklyCompletion={diningStreak.weeklyCompletion}
                  onViewAll={onViewStreak}
                  onSignUp={onSignUpStreak}
                />
              </Col>
            )}
            {hasChallengeData && (
              <Col md={6} lg={6} xxl={colSize}>
                <PassportChallengeCard
                  loggedIn={isLoggedIn}
                  name={passportChallenge.name}
                  description={passportChallenge.description}
                  visited={passportChallenge.visited}
                  total={passportChallenge.total}
                  pointsReward={passportChallenge.pointsReward}
                  onViewAll={onViewChallenge}
                />
              </Col>
            )}
          </>
        )}
        <Col {...lastCardColProps}>
          <RewardPointsCard
            loggedIn={isLoggedIn}
            points={rewardPoints.points}
            cashbackPercent={accountState.cashbackPercent}
            tiers={rewardPoints.tiers}
            onRedeem={onRedeemPoints}
            onJoinFree={onJoinFree}
          />
        </Col>
      </Row>
    </HorizontalScroll>
  );
};

TopRewardsWidget.propTypes = {
  data: PropTypes.shape({
    accountState: PropTypes.shape({
      connected: PropTypes.bool,
      cashbackPercent: PropTypes.number,
      accountNumber: PropTypes.string,
    }),
    diningStreak: PropTypes.shape({
      weeksCompleted: PropTypes.number,
      totalWeeks: PropTypes.number,
      pointsReward: PropTypes.number,
    }),
    passportChallenge: PropTypes.shape({
      visited: PropTypes.number,
      total: PropTypes.number,
      pointsReward: PropTypes.number,
    }),
    rewardPoints: PropTypes.shape({
      points: PropTypes.number,
      tiers: PropTypes.arrayOf(
        PropTypes.shape({
          points: PropTypes.number.isRequired,
          value: PropTypes.number.isRequired,
        }),
      ),
    }),
  }).isRequired,
  isLoggedIn: PropTypes.bool,
  onConnectAccount: PropTypes.func,
  onSettings: PropTypes.func,
  onViewStreak: PropTypes.func,
  onSignUpStreak: PropTypes.func,
  onViewChallenge: PropTypes.func,
  onRedeemPoints: PropTypes.func,
  onJoinFree: PropTypes.func,
};

TopRewardsWidget.defaultProps = {
  isLoggedIn: true,
  onConnectAccount: () => {},
  onSettings: () => {},
  onViewStreak: () => {},
  onSignUpStreak: () => {},
  onViewChallenge: () => {},
  onRedeemPoints: () => {},
  onJoinFree: () => {},
};

export default TopRewardsWidget;
